import { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api, formatApiError } from "@/lib/api";
import MaterialFile from "@/components/MaterialFile";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import AdminDeleteConfirmation from "@/components/AdminDeleteConfirmation";

const empty = { title: "", youtube_url: "", description: "", category: "Tática", audience: "Todos" };

export default function AdminVideos() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [source, setSource] = useState("youtube");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(0);
  const load = () => api.get("/videos").then(r => setItems(r.data)).catch(() => toast.error("Não foi possível carregar a biblioteca."));
  useEffect(() => { load(); }, []);
  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!form.title.trim()) { toast.error("Informe o título do material."); return; }
    if (source === "file") {
      if (!file) { toast.error("Selecione um arquivo."); return; }
      const ext = file.name.split(".").pop().toLowerCase();
      if (!["pdf", "mp4", "webm"].includes(ext)) { toast.error("Envie um arquivo PDF, MP4 ou WebM."); return; }
      const limit = ext === "pdf" ? 20 : 100;
      if (!file.size || file.size > limit * 1024 * 1024) {
        toast.error(`Envie um arquivo não vazio de até ${limit} MB.`); return;
      }
    } else {
      try {
        const u = new URL(form.youtube_url);
        const host = u.hostname.replace(/^www\./, "");
        const id = host === "youtu.be" ? u.pathname.split("/")[1] : (u.searchParams.get("v") || u.pathname.split("/")[2]);
        if (!['http:', 'https:'].includes(u.protocol) || !['youtube.com', 'm.youtube.com', 'youtu.be'].includes(host) || !/^[A-Za-z0-9_-]{11}$/.test(id || "")) throw new Error();
      } catch { toast.error("Informe uma URL válida de vídeo do YouTube"); return; }
    }
    setSaving(true); setProgress(0);
    try {
      if (source === "file") {
        const data = new FormData();
        data.append("file", file);
        ["title", "description", "category"].forEach(key => data.append(key, form[key]));
        await api.post("/videos/upload", data, {
          onUploadProgress: event => {
            if (event.total) setProgress(Math.round(event.loaded * 100 / event.total));
          },
        });
      } else { await api.post("/videos", form); }
      toast.success("Material adicionado"); setForm(empty); setFile(null); setOpen(false); load();
    } catch (error) { toast.error(formatApiError(error.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const remove = async (id) => {
    try { await api.delete(`/videos/${id}`); load(); }
    catch { toast.error("Não foi possível excluir o material."); }
  };
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <AppLayout kind="admin">
      <div className="flex justify-between items-center mb-8 flex-wrap gap-3">
        <h1 className="text-3xl sm:text-4xl text-white font-semibold">Biblioteca de Materiais</h1>
        <Dialog open={open} onOpenChange={value => { if (!saving) { setOpen(value); setFile(null); } }}>
          <DialogTrigger asChild><Button data-testid="admin-add-video-btn" className="bg-amber-500 text-[#071A33] hover:bg-amber-400 font-semibold">+ Novo Material</Button></DialogTrigger>
          <DialogContent className="bg-[#071A33] border-amber-500/30 text-white max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Novo Material</DialogTitle></DialogHeader>
            <form onSubmit={save} className="space-y-3">
              <fieldset disabled={saving} className="space-y-3">
              <p className="text-sm text-slate-400">Os materiais desta biblioteca ficam disponíveis para todos os alunos.</p>
              <div><Label htmlFor="material-source">Tipo de envio</Label>
                <select id="material-source" value={source} onChange={e => { setSource(e.target.value); setFile(null); }}
                  className="w-full rounded-md bg-[#0D2A52] border border-amber-500/20 p-2 text-white">
                  <option value="youtube">Link do YouTube</option>
                  <option value="file">Vídeo ou apostila em PDF</option>
                </select>
              </div>
              <div><Label htmlFor="material-title" className="text-slate-300">Título</Label><Input id="material-title" value={form.title} required maxLength={200} onChange={(e) => set("title", e.target.value)} className="bg-[#0D2A52] border-amber-500/20 text-white" /></div>
              {source === "youtube" ? <div><Label className="text-slate-300">URL do YouTube</Label><Input value={form.youtube_url} required onChange={(e) => set("youtube_url", e.target.value)} className="bg-[#0D2A52] border-amber-500/20 text-white" /></div> :
                <div><Label htmlFor="material-file" className="text-slate-300">Arquivo</Label>
                  <Input id="material-file" type="file" accept=".pdf,.mp4,.webm" required
                    onChange={e => setFile(e.target.files?.[0] || null)} className="bg-[#0D2A52] border-amber-500/20 text-white" />
                  <p className="text-xs text-slate-400 mt-1">Vídeos MP4/WebM: até 100 MB. Apostilas e PDFs: até 20 MB.</p>
                </div>}
              <div><Label htmlFor="material-category" className="text-slate-300">Categoria</Label><Input id="material-category" value={form.category} maxLength={100} onChange={(e) => set("category", e.target.value)} className="bg-[#0D2A52] border-amber-500/20 text-white" /></div>
              <div><Label htmlFor="material-description" className="text-slate-300">Descrição</Label><Textarea id="material-description" rows={3} value={form.description} maxLength={5000} onChange={(e) => set("description", e.target.value)} className="bg-[#0D2A52] border-amber-500/20 text-white" /></div>
              <Button type="submit" disabled={saving} className="w-full bg-amber-500 text-[#071A33] hover:bg-amber-400 font-semibold">
                {saving ? (source === "file" ? (progress < 100 ? `Enviando… ${progress}%` : "Salvando arquivo…") : "Salvando…") : "Salvar"}
              </Button>
              </fieldset>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map(v => (
          <Card key={v.id} className="bg-transparent gsx-card-dark p-5">
            <div className="text-xs text-amber-300 uppercase tracking-widest">{v.category}</div>
            <div className="text-white font-semibold mt-1">{v.title}</div>
            {v.file_id ? <MaterialFile material={v} /> : <a href={v.youtube_url} target="_blank" rel="noreferrer" className="text-xs text-amber-400 mt-2 block truncate">{v.youtube_url}</a>}
            <AdminDeleteConfirmation itemName={v.title || "este vídeo"} onConfirm={() => remove(v.id)}><Button size="sm" variant="destructive" className="mt-3">Excluir</Button></AdminDeleteConfirmation>
          </Card>
        ))}
      </div>
    </AppLayout>
  );
}
